<script setup lang="ts">
import { computed, type Component } from 'vue';

interface Brand {
  name: string;
  sub?: string;
  /**
   * The signed-in account or mailbox the app works in („anna@demo.local“).
   * Unlike `sub` (a tagline in small caps) it keeps its spelling, so an address
   * reads as written; a long one is cut with an ellipsis and shown in full as
   * the tooltip. Takes the place of `sub` when both are set.
   */
  account?: string;
  /** Logo: 1-2 char string (rendered inside the gradient tile) or a Component */
  logo: string | Component;
}

const props = defineProps<{
  brand: Brand;
  /** Size variant — `md` (sidebar default) or `sm` (compact, e.g. in panels) */
  size?: 'md' | 'sm';
}>();

defineEmits<{
  (e: 'click'): void;
}>();

const logoIsString = computed(() => typeof props.brand.logo === 'string');
const sizeClass = computed(() => `size-${props.size ?? 'md'}`);
</script>

<template>
  <button type="button" class="cn-brand" :class="sizeClass" @click="$emit('click')">
    <div class="cn-brand-logo">
      <template v-if="logoIsString">{{ brand.logo }}</template>
      <component :is="brand.logo" v-else :class="size === 'sm' ? 'nxt-icon-md' : 'nxt-icon-lg'" />
    </div>
    <div class="cn-brand-text">
      <div class="cn-brand-name">{{ brand.name }}</div>
      <div v-if="brand.account" class="cn-brand-account" :title="brand.account">
        {{ brand.account }}
      </div>
      <div v-else-if="brand.sub" class="cn-brand-sub">{{ brand.sub }}</div>
    </div>
  </button>
</template>

<style scoped>
.cn-brand {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-md);
  cursor: pointer;
  transition: background var(--nxt-motion-fast);
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  color: inherit;
}
.cn-brand:hover {
  background: var(--nxt-brand-subtle-bg);
}

.cn-brand-logo {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: linear-gradient(
    135deg in hsl,
    var(--nxt-brand-bg) 0%,
    hsl(from var(--nxt-brand-bg) h s calc(l + 15)) 100%
  );
  color: var(--nxt-brand-fg);
  font-weight: var(--nxt-font-weight-bold);
  font-family: var(--nxt-font);
  box-shadow: var(--nxt-shadow-md);
}

.cn-brand.size-md .cn-brand-logo {
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  border-radius: var(--nxt-radius-md);
  font-size: var(--nxt-font-size-md);
}
.cn-brand.size-sm .cn-brand-logo {
  width: calc(var(--nxt-size-unit) * 1.625);
  height: calc(var(--nxt-size-unit) * 1.625);
  border-radius: var(--nxt-radius-sm);
  font-size: var(--nxt-font-size-sm);
}

.cn-brand-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.cn-brand-name {
  font-weight: var(--nxt-font-weight-semibold);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cn-brand.size-sm .cn-brand-name {
  font-size: var(--nxt-font-size-sm);
}

.cn-brand-account {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cn-brand-sub {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
